import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { SiteSurveyResponseDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';

interface SiteSurveyResponseFormProps {
  initialValues: SiteSurveyResponseDTO;
  onSubmit: (data: SiteSurveyResponseDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information'
  }
} as const;

// The multi-select fields are stored as semicolon-joined strings on the DTO;
// these convert to/from the array shape the multiselect control needs.
const toArray = (value?: string): string[] => (value ? value.split(';').filter(Boolean) : []);
const toJoined = (values: string[]): string => values.join(';');

const ALERT_TRIGGERS_OPTIONS = [
  { value: 'collars', label: 'GPS collars on the animals' },
  { value: 'camera_traps', label: 'Camera traps' },
  { value: 'fence_alarm', label: 'Fence alarm' },
  { value: 'gate_sensors', label: 'Gate or entrance sensors' },
  { value: 'fixed_thermal', label: 'Fixed thermal or night cameras on poles' },
  { value: 'patrol_radio', label: 'A patrol team calling it in on the radio' },
  { value: 'other', label: 'Something else' },
  { value: 'none', label: 'Nothing automatic, we rely on patrols' },
];

const DRONE_MODEL_DJI_OPTIONS = [
  { value: 'm3t', label: 'Mavic 3 Thermal (M3T)' },
  { value: 'm3e', label: 'Mavic 3 Enterprise' },
  { value: 'm30t', label: 'Matrice 30T (M30T)' },
  { value: 'm300_m350', label: 'Matrice 300 or 350 RTK' },
  { value: 'consumer_no_thermal', label: 'A Mavic or Phantom without a heat camera' },
  { value: 'other', label: 'Something else, not on this list' },
  { value: 'unsure', label: 'Not sure' },
];

const CAA_APPROVALS_OPTIONS = [
  { value: 'roc', label: 'Operating certificate (ROC)' },
  { value: 'night', label: 'Approval to fly at night' },
  { value: 'bvlos', label: 'Approval to fly beyond visual line of sight' },
  { value: 'rpl', label: 'Licensed pilot on the team (RPL)' },
  { value: 'registered', label: 'Drones registered with CAA' },
  { value: 'none', label: 'None of these' },
  { value: 'unsure', label: 'Not sure' },
];

const NIGHT_RESPONDER_OPTIONS = [
  { value: 'staff_onsite', label: 'Staff living on the property' },
  { value: 'staff_offsite', label: 'Staff living off site' },
  { value: 'contracted', label: 'Contracted security' },
  { value: 'apu', label: 'Anti poaching unit' },
  { value: 'daylight_only', label: 'Nobody goes out until it is light' },
];

const SiteSurveyResponseForm: React.FC<SiteSurveyResponseFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<SiteSurveyResponseDTO>(initialValues);

  // Mirrors the survey's own shown-when rules (spec section 05), so the edit
  // form only shows a companion/"other" field when its parent answer applies.
  const showEarthrangerDetails = formData.usesEarthranger === 'yes';
  const showAlertTriggersOther = toArray(formData.alertTriggers).includes('other');
  const showStaffTrackingMethod =
    formData.tracksStaffPositions === 'all_staff' || formData.tracksStaffPositions === 'some_staff';
  const showDroneModelDji = formData.droneOwnership === 'dji';
  const showDroneModelOther = formData.droneOwnership === 'other';
  const showLaunchMethod = !!formData.droneOwnership && formData.droneOwnership !== 'none';

  const validationRules: ValidationRules = {
    submittedUtc: [
      { required: true, message: 'Submitted Utc is required' },
      { custom: (value) => !isNaN(Date.parse(value)), message: 'Submitted Utc must be a valid date' }
    ],
    formVersion: [
      { required: true, message: 'Form Version is required' }
    ],
    phone: [
      { required: true, message: 'Phone is required' }
    ],
    usesEarthranger: [
      { required: true, message: 'Uses Earthranger is required' }
    ],
    erSurvivesOutage: [
      { required: true, message: 'Er Survives Outage is required' }
    ],
    alertTriggers: [
      { required: true, message: 'Alert Triggers is required' }
    ],
    tracksStaffPositions: [
      { required: true, message: 'Tracks Staff Positions is required' }
    ],
    droneOwnership: [
      { required: true, message: 'Drone Ownership is required' }
    ],
    caaApprovals: [
      { required: true, message: 'Caa Approvals is required' }
    ],
    nightResponder: [
      { required: true, message: 'Night Responder is required' }
    ],
    nightResponseTime: [
      { required: true, message: 'Night Response Time is required' }
    ],
    firstSiteInterest: [
      { required: true, message: 'First Site Interest is required' }
    ]
  };

  const { errors, validate } = useFormValidation<SiteSurveyResponseDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="phone"
          label="Phone"
          type="text"
          value={formData.phone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Phone"
          required={true}
          error={errors.phone}
        />
        <FormField
          id="usesEarthranger"
          label="Uses Earthranger"
          type="text"
          value={formData.usesEarthranger || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Uses Earthranger"
          required={true}
          error={errors.usesEarthranger}
        />
        {showEarthrangerDetails && (
          <>
            <FormField
              id="erSurvivesOutage"
              label="Er Survives Outage"
              type="text"
              value={formData.erSurvivesOutage || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Er Survives Outage"
              required={true}
              error={errors.erSurvivesOutage}
            />
            <FormField
              id="erAdminContact"
              label="Er Admin Contact"
              type="text"
              value={formData.erAdminContact || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Er Admin Contact"
              required={false}
              error={errors.erAdminContact}
            />
          </>
        )}
        <FormField
          id="alertTriggers"
          label="Alert Triggers"
          type="multiselect"
          value={toArray(formData.alertTriggers)}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              alertTriggers: toJoined(e.target.value as unknown as string[]),
            }))
          }
          options={ALERT_TRIGGERS_OPTIONS}
          placeholder="Select alert triggers..."
          required={true}
          error={errors.alertTriggers}
        />
        {showAlertTriggersOther && (
          <FormField
            id="alertTriggersOther"
            label="Alert Triggers Other"
            type="text"
            value={formData.alertTriggersOther || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Alert Triggers Other"
            required={false}
            error={errors.alertTriggersOther}
          />
        )}
        <FormField
          id="tracksStaffPositions"
          label="Tracks Staff Positions"
          type="text"
          value={formData.tracksStaffPositions || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Tracks Staff Positions"
          required={true}
          error={errors.tracksStaffPositions}
        />
        {showStaffTrackingMethod && (
          <FormField
            id="staffTrackingMethod"
            label="Staff Tracking Method"
            type="text"
            value={formData.staffTrackingMethod || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Staff Tracking Method"
            required={false}
            error={errors.staffTrackingMethod}
          />
        )}
        <FormField
          id="droneOwnership"
          label="Drone Ownership"
          type="text"
          value={formData.droneOwnership || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Drone Ownership"
          required={true}
          error={errors.droneOwnership}
        />
        {showDroneModelDji && (
          <FormField
            id="droneModelDji"
            label="Drone Model Dji"
            type="multiselect"
            value={toArray(formData.droneModelDji)}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                droneModelDji: toJoined(e.target.value as unknown as string[]),
              }))
            }
            options={DRONE_MODEL_DJI_OPTIONS}
            placeholder="Select DJI models..."
            required={false}
            error={errors.droneModelDji}
          />
        )}
        {showDroneModelOther && (
          <FormField
            id="droneModelOther"
            label="Drone Model Other"
            type="text"
            value={formData.droneModelOther || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Drone Model Other"
            required={false}
            error={errors.droneModelOther}
          />
        )}
        {showLaunchMethod && (
          <FormField
            id="launchMethod"
            label="Launch Method"
            type="text"
            value={formData.launchMethod || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Launch Method"
            required={true}
            error={errors.launchMethod}
          />
        )}
        <FormField
          id="caaApprovals"
          label="Caa Approvals"
          type="multiselect"
          value={toArray(formData.caaApprovals)}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              caaApprovals: toJoined(e.target.value as unknown as string[]),
            }))
          }
          options={CAA_APPROVALS_OPTIONS}
          placeholder="Select CAA approvals..."
          required={true}
          error={errors.caaApprovals}
        />
        <FormField
          id="nightResponder"
          label="Night Responder"
          type="multiselect"
          value={toArray(formData.nightResponder)}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              nightResponder: toJoined(e.target.value as unknown as string[]),
            }))
          }
          options={NIGHT_RESPONDER_OPTIONS}
          placeholder="Select night responders..."
          required={true}
          error={errors.nightResponder}
        />
        <FormField
          id="nightResponseTime"
          label="Night Response Time"
          type="text"
          value={formData.nightResponseTime || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Night Response Time"
          required={true}
          error={errors.nightResponseTime}
        />
        <FormField
          id="firstSiteInterest"
          label="First Site Interest"
          type="text"
          value={formData.firstSiteInterest || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter First Site Interest"
          required={true}
          error={errors.firstSiteInterest}
        />

      </FormSection>
    </Form>
  );
};

export default SiteSurveyResponseForm;
